/**
 * AgentSidebarWidget — the app's ONE sidebar component.
 *
 * Built on shadcn's `Sidebar` primitive (`ui/src/shared/ui/sidebar.tsx`,
 * collapsible="icon" mode) instead of two separately-mounted trees (a full
 * sidebar + a hand-rolled "slim rail"). The same content renders in both
 * states; collapsing only changes CSS (`group-data-[collapsible=icon]`), so
 * every actionable item (New Chat, Chat History, Shared Chats, Workflows) stays
 * visible as a tooltipped icon when collapsed, not just a hand-picked subset.
 *
 * Key contract: does NOT fetch threads itself. It consumes useThreadsQuery
 * (via ThreadHistoryList) which is already shared with the chat workspace.
 * This eliminates the duplicate history-fetch that existed in the legacy sidebar.
 */

import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Clock,
  CircleHelp,
  BookOpen,
  Loader2,
  Trash2,
  Share2,
  Plus,
  History,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  Home,
  Plug,
  Bot,
  Pin,
  PinOff,
  Pencil,
  Zap,
  PlayCircle,
} from 'lucide-react';
import adobeLogo from '../../../assets/images/Adobe_icon_RGB_red.svg';
import argusLogo from '../../../assets/images/acs-argus-mark-192.png';
import { Button } from '@/shared/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/shared/ui/sidebar';
import { Input } from '@/shared/ui/input';
import { cn } from '@/shared/lib/cn';
import { isFeatureEnabled } from '@/shared/config';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';
import {
  useDeleteThread,
  useAgentThreadsInfiniteQuery,
  useSharedWithMeQuery,
  useUpdatePin,
  useUpdateTitle,
} from '@/entities/thread';
import { useCancelSchedule, type ScheduleView } from '@/entities/schedule';
import { useSchedulesQuery } from '@/features/schedules';
import { ShareThreadDialog } from '@/features/chat/share-thread';
import { log } from '@/shared/logger';
import type { ThreadSummaryView } from '@/entities/thread';
import { devStatusColor } from '@/entities/agent/ui/DevStatusBadge';
import { UserMenuWidget } from '@/widgets/user-menu';
import { NotificationsWidget } from '@/widgets/notifications';
import { useSettingsAccess } from '@/components/settings/access';
import { APP_DEMO_URL, APP_HELP_DOC_URL, SUPPORT_SLACK_URL } from '@/shared/helpLinks';
import {
  useChatViewStore,
  useChatWorkspaceController,
  useChatWorkspaceStore,
} from '@/shared/chat-workspace';

// Shared classes for the three thread-list sections (Chat History, Shared
// Chats, Workflows) — kept as constants/helpers instead of repeating the same
// utility strings three times.
const SESSION_LIST_CLASS = 'flex flex-col gap-1 max-h-80 overflow-x-hidden overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
const GROUP_LABEL_CLASS = 'px-2.5 pt-2 pb-1 text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground';
const SUBSECTION_HEADER_CLASS = 'flex items-center gap-2 px-2.5 py-0.5 text-xs font-medium text-muted-foreground';
const SUBSECTION_CLASS = 'flex flex-col gap-1';
const SESSION_ROW_INNER_CLASS = 'flex w-full min-w-0 items-center gap-1.5 overflow-hidden';
const SESSION_ACTION_CLASS = 'absolute right-0 top-1/2 -translate-y-1/2';
const DELETE_ACTION_CLASS = 'opacity-0 transition-opacity duration-150 group-hover:opacity-100';
const SURFACE_ROUTES = new Set(['/skills', '/plugins']);
const PAGE_SIZE = 6;

// All icon-only header buttons share this size so the collapse toggle and
// brand row line up visually in both sidebar states.
const HEADER_ICON_SIZE = 'icon-sm';
const BRAND_LOGO_CLASS = 'h-7 w-auto shrink-0';
const BRAND_TEXT_CLASS = 'truncate text-base font-bold text-foreground group-data-[collapsible=icon]:hidden';
const COLLAPSED_ICON_BUTTON_CLASS = 'group-data-[collapsible=icon]:!size-7';
const FLAT_SIDEBAR_BUTTON_CLASS = 'px-2.5 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold data-[active=true]:shadow-none';

function chatItemClass(isActive: boolean): string {
  return cn(
    'group relative flex w-full min-w-0 flex-col gap-0 rounded-md pl-2.5 pr-1 py-1.5 cursor-pointer transition-colors hover:bg-muted/35 hover:text-foreground',
    isActive && 'bg-muted/45 text-foreground',
  );
}

function sessionLabelClass(isActive: boolean): string {
  return cn(
    'min-w-0 flex-1 overflow-hidden pr-4 text-xs leading-5 font-normal text-ellipsis whitespace-nowrap',
    isActive && 'text-foreground',
  );
}

function surfaceReturnPath(location: ReturnType<typeof useLocation>): string {
  if (SURFACE_ROUTES.has(location.pathname)) {
    const state = location.state as { from?: unknown } | null;
    return typeof state?.from === 'string' ? state.from : '/';
  }
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * One sidebar section: a `SidebarMenuButton` trigger (always visible, icon +
 * label; label collapses to a tooltip when the sidebar is in icon mode) plus
 * disclosure content that only renders when the sidebar is expanded — so
 * collapsing never hand-picks which triggers survive, only hides the
 * (non-icon-representable) list content beneath them.
 */
/**
 * Shared destructive-confirm dialog — replaces three hand-rolled inline
 * popovers (each with its own ref + document mousedown listener for
 * click-outside dismissal) with one shadcn AlertDialog, which gives a focus
 * trap, ESC-to-close, and click-outside dismissal for free.
 */
function ConfirmDialog({
  isOpen,
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
}: {
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{children}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type Props = {
  selectedAgentId: string | null;
  canSwitchDevPersona?: boolean;
  devPersona?: string;
  onSwitchDevPersona?: (key: string) => void;
};

function formatScheduledTime(schedule: ScheduleView): string {
  if (!schedule.nextRunAt) {
    return schedule.cron ? `Scheduled: ${schedule.cron}` : 'Not scheduled';
  }

  const date = new Date(schedule.nextRunAt);
  if (Number.isNaN(date.getTime())) {
    return `Scheduled: ${schedule.nextRunAt}`;
  }

  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
  return `Scheduled: ${formatted}`;
}

/** Brand row + collapse toggle — the sidebar's header, same in both states. */
function SidebarBrandHeader() {
  const navigate = useNavigate();
  const { open, setOpen } = useSidebar();
  const { resetWorkspace } = useChatWorkspaceController();

  return (
    <div
      className={cn(
        'flex h-15 shrink-0 items-center justify-between gap-2.5 px-2.5',
        !open && 'h-auto flex-col justify-center gap-1.5 px-0 py-2.5',
      )}
    >
      <button
        type="button"
        className={cn(
          'inline-flex min-w-0 flex-1 items-center gap-2.5 rounded-md border-0 bg-transparent px-0.5 py-1 cursor-pointer group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0',
          COLLAPSED_ICON_BUTTON_CLASS,
        )}
        onClick={() => { resetWorkspace(); navigate('/'); }}
        aria-label="Go to agent hub home"
      >
        <img src={adobeLogo} alt="" aria-hidden className={BRAND_LOGO_CLASS} />
        <span className={BRAND_TEXT_CLASS}>ACS Agent Hub</span>
      </button>
      {/* Kept visible (not hidden) when collapsed — SidebarRail's edge-drag
          affordance alone isn't discoverable enough as the only way to expand. */}
      <Button
        variant="ghost"
        size={HEADER_ICON_SIZE}
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Close sidebar' : 'Open sidebar'}
        className={cn('shrink-0', COLLAPSED_ICON_BUTTON_CLASS)}
      >
        {open ? <PanelLeftClose /> : <PanelLeftOpen />}
      </Button>
    </div>
  );
}

const DEV_PERSONAS = [
  { id: 'default', label: 'Default' },
  { id: 'esg_retention_non_admin', label: 'ESG + Retention (Non-Admin)' },
  { id: 'esg_retention_admin', label: 'ESG + Retention (Admin)' },
  { id: 'all_agents_non_admin', label: 'All Agents (Non-Admin)' },
  { id: 'no_agents', label: 'No Agents' },
];

function FooterControls() {
  return (
    <SidebarMenu className="gap-1.5">
      <SidebarMenuItem>
        <UserMenuWidget />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AgentSidebarWidget({
  selectedAgentId,
  canSwitchDevPersona = false,
  devPersona = 'default',
  onSwitchDevPersona = () => {},
}: Props) {
  const { setOpen: setSidebarOpen } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { agents } = useAgentCatalog();
  const { canAccessPlugins } = useSettingsAccess();
  const notificationsEnabled = isFeatureEnabled('slack');
  const schedulesEnabled = isFeatureEnabled('schedules');
  const {
    clearSelection,
    clearThread,
    selectAgent,
    selectThread,
    startNewChat,
  } = useChatWorkspaceController();
  // Build a map from agentId → devStatus for thread dot coloring
  const statusMap = useMemo(
    () => new Map(agents.map((a) => [a.id, a.devStatus ?? null])),
    [agents],
  );

  const selectedThreadId = useChatViewStore((state) => (
    selectedAgentId ? state.agentThreads[selectedAgentId] ?? null : null
  ));
  const creatingNewChatAgentIds = useChatWorkspaceStore((state) => state.creatingNewChatAgentIds);
  const activeThreadId = selectedThreadId;
  const isCreatingNewChat = selectedAgentId
    ? creatingNewChatAgentIds.includes(selectedAgentId)
    : false;

  const {
    data: threadPages,
    isLoading: isLoadingHistory,
    hasNextPage: hasMoreThreads,
    isFetchingNextPage: isLoadingMoreThreads,
    fetchNextPage: fetchMoreThreads,
  } = useAgentThreadsInfiniteQuery(selectedAgentId, PAGE_SIZE);
  const { data: sharedThreads, isLoading: isLoadingShared } = useSharedWithMeQuery(50);

  const { deleteThread } = useDeleteThread();
  const { updatePin } = useUpdatePin();
  const { updateTitle } = useUpdateTitle();

  // Scheduled workflows for the selected agent (scheduling is a platform capability
  // available to every agent). Surfaced under Chat History in the sidebar.
  const schedulesQuery = useSchedulesQuery({
    agentId: selectedAgentId ?? undefined,
    includeCancelled: false,
    limit: 50,
    enabled: schedulesEnabled,
  });
  const jobs = schedulesQuery.data?.schedules ?? [];
  const { cancelSchedule } = useCancelSchedule();

  const [sharedLimit, setSharedLimit] = useState(PAGE_SIZE);
  const [jobsLimit, setJobsLimit] = useState(PAGE_SIZE);
  useEffect(() => {
    setSharedLimit(PAGE_SIZE);
    setJobsLimit(PAGE_SIZE);
  }, [selectedAgentId]);

  const filteredThreads: ReadonlyArray<ThreadSummaryView> = useMemo(() => {
    const seen = new Set<string>();
    return (threadPages?.pages ?? [])
      .flatMap((page) => page.threads)
      .filter((thread) => {
        if (!thread.hasMessages || thread.agentId !== selectedAgentId || seen.has(thread.id)) {
          return false;
        }
        seen.add(thread.id);
        return true;
      });
  }, [threadPages, selectedAgentId]);

  const filteredSharedThreads: ReadonlyArray<ThreadSummaryView> = useMemo(() => {
    const all = sharedThreads ?? [];
    if (!selectedAgentId) return all;
    return all.filter((t) => t.agentId === selectedAgentId);
  }, [sharedThreads, selectedAgentId]);

  const visibleThreads = filteredThreads;
  const visibleSharedThreads = filteredSharedThreads.slice(0, sharedLimit);
  const visibleJobs = jobs.slice(0, jobsLimit);
  const hasMoreSharedThreads = filteredSharedThreads.length > sharedLimit;
  const hasMoreJobs = jobs.length > jobsLimit;

  // Group visible threads by recency
  type ThreadGroup = { label: string; threads: ReadonlyArray<ThreadSummaryView> };
  const groupedThreads = useMemo((): ThreadGroup[] => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfWeek = startOfToday - (now.getDay() || 7) * 86400000 + 86400000;
    const groups: Record<string, ThreadSummaryView[]> = { Pinned: [], Today: [], 'This week': [], Earlier: [] };
    for (const t of visibleThreads) {
      if (t.pinnedAt !== null) {
        groups.Pinned.push(t);
        continue;
      }
      const ts = t.updatedAt ? new Date(t.updatedAt).getTime() : 0;
      if (ts >= startOfToday) groups['Today'].push(t);
      else if (ts >= startOfWeek) groups['This week'].push(t);
      else groups['Earlier'].push(t);
    }
    return Object.entries(groups)
      .filter(([, threads]) => threads.length > 0)
      .map(([label, threads]) => ({ label, threads }));
  }, [visibleThreads]);

  const [deleteSingleConfirm, setDeleteSingleConfirm] = useState<string | null>(null);
  const [deleteJobConfirm, setDeleteJobConfirm] = useState<string | null>(null);
  const [shareThreadId, setShareThreadId] = useState<string | null>(null);
  const [renameThreadId, setRenameThreadId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const currentAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );
  const sortedAgents = useMemo(
    () => [...agents]
      .sort((a, b) => {
      const aIsArgus = a.id === 'argus' || a.displayName.toLowerCase() === 'argus';
      const bIsArgus = b.id === 'argus' || b.displayName.toLowerCase() === 'argus';
      if (aIsArgus && !bIsArgus) return -1;
      if (!aIsArgus && bIsArgus) return 1;
      return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
    }),
    [agents],
  );
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isPluginsActive = location.pathname.startsWith('/plugins');
  const isSkillsActive = location.pathname.startsWith('/skills');
  const isHomeActive = location.pathname === '/';
  const showConversationSections = Boolean(selectedAgentId) && !isSettingsRoute;
  const handleGoHome = () => {
    clearSelection();
    navigate('/');
  };
  const handleAgentSwitch = (agentId: string) => {
    // The agent dropdown is navigation: preserve the per-agent thread mapping
    // so returning to an agent resumes the chat this tab last had open.
    selectAgent(agentId);
    navigate(`/agents/${agentId}`);
  };
  const handleSurfaceNavigation = (path: '/skills' | '/plugins') => {
    navigate(path, {
      replace: SURFACE_ROUTES.has(location.pathname),
      state: { from: surfaceReturnPath(location) },
    });
  };

  const handleDeleteThread = async (thread: ThreadSummaryView) => {
    const agentId = thread.agentId ?? selectedAgentId;
    if (agentId && thread.id === activeThreadId) {
      clearThread(agentId, thread.id);
    }
    await deleteThread(thread.id).catch((err) => {
      log.warn('thread_delete_failed', { error: err });
    });
  };

  const handlePinThread = async (thread: ThreadSummaryView) => {
    await updatePin({ threadId: thread.id, pinned: thread.pinnedAt === null }).catch((err) => {
      log.warn('thread_pin_update_failed', { error: err });
    });
  };

  const openRenameDialog = (thread: ThreadSummaryView) => {
    setRenameThreadId(thread.id);
    setRenameDraft(thread.title ?? thread.id);
  };

  const handleRenameThread = async () => {
    const title = renameDraft.trim();
    const thread = filteredThreads.find((t) => t.id === renameThreadId);
    setRenameThreadId(null);
    if (!thread || !title || title === thread.title) return;
    await updateTitle({ threadId: thread.id, title }).catch((err) => {
      log.warn('thread_rename_failed', { error: err });
    });
  };

  return (
    <Sidebar
      variant="sidebar"
      collapsible="icon"
      className="[--sidebar-width:16rem] [--sidebar-width-icon:3rem] border-r border-sidebar-border/40"
    >
      <SidebarHeader className="gap-0 border-b border-sidebar-border px-0.5 py-0 group-data-[collapsible=icon]:items-center">
        <SidebarBrandHeader />
        {canSwitchDevPersona && (
          <div className="px-3 pb-3 pt-1 group-data-[collapsible=icon]:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton variant="outline" className="h-8 font-medium">
                  <span className="truncate">{DEV_PERSONAS.find((persona) => persona.id === devPersona)?.label ?? 'Default'}</span>
                  <ChevronDown className="ml-auto opacity-60" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="bottom" className="min-w-56 rounded-lg p-1">
                {DEV_PERSONAS.map((persona) => (
                  <DropdownMenuItem
                    key={persona.id}
                    onSelect={() => onSwitchDevPersona(persona.id)}
                    className="gap-2 rounded-md text-xs"
                  >
                    <Check className={cn('opacity-0', persona.id === devPersona && 'opacity-100')} />
                    <span>{persona.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {selectedAgentId && currentAgent ? (
          <>
            <SidebarSeparator className="mx-3" />
            <div className="px-2 py-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                          tooltip="Switch active agent"
                          variant="outline"
                          className={cn('h-auto min-h-11 py-1.5', COLLAPSED_ICON_BUTTON_CLASS)}
                        >
                          {currentAgent.id === 'argus' ? (
                            <img src={argusLogo} alt="" aria-hidden className="size-5 shrink-0 object-contain" />
                          ) : (
                            <Bot className="shrink-0" />
                          )}
                          <span className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                          <span className="block truncate text-xs font-medium text-muted-foreground">Agent</span>
                          <span className="block truncate text-xs font-medium text-foreground">{currentAgent.displayName}</span>
                        </span>
                        <ChevronDown className="ml-auto opacity-60 group-data-[collapsible=icon]:hidden" />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="bottom" className="min-w-64 rounded-lg p-1">
                      {sortedAgents.map((agent) => (
                        <DropdownMenuItem
                          key={agent.id}
                          onSelect={() => handleAgentSwitch(agent.id)}
                          className="gap-2 rounded-md text-xs"
                        >
                          <Check className={cn('opacity-0', agent.id === selectedAgentId && 'opacity-100')} />
                          {agent.id === 'argus' && (
                            <img src={argusLogo} alt="" aria-hidden className="size-4 shrink-0 object-contain" />
                          )}
                          <span className="truncate">{agent.displayName}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </div>
          </>
        ) : null}
        {selectedAgentId && (
          <>
            <SidebarSeparator className="mx-3" />
            <div className="px-2 py-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="New Chat"
                    disabled={isCreatingNewChat}
                    onClick={() => { void startNewChat(selectedAgentId); }}
                    className={cn(
                      'justify-center rounded-md font-medium !bg-primary !text-primary-foreground hover:!bg-primary/90 active:!bg-primary/90 data-active:!bg-primary/90',
                      COLLAPSED_ICON_BUTTON_CLASS,
                    )}
                  >
                    {isCreatingNewChat ? <Loader2 className="animate-spin" /> : <Plus />}
                    <span className="group-data-[collapsible=icon]:hidden">New Chat</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </div>
          </>
        )}
      </SidebarHeader>
      <SidebarContent className="pl-2.5 pr-2 py-2.5 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        <SidebarGroup className="p-0 pb-2.5">
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="All Agents"
                  isActive={isHomeActive}
                  className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                  onClick={handleGoHome}
                >
                  <Home />
                  <span className="truncate">All Agents</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {canAccessPlugins ? (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Plugins"
                    isActive={isPluginsActive}
                    className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                    onClick={() => handleSurfaceNavigation('/plugins')}
                  >
                    <Plug />
                    <span className="truncate">Plugins</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Skill Registry"
                  isActive={isSkillsActive}
                  className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                  onClick={() => handleSurfaceNavigation('/skills')}
                >
                  <Zap />
                  <span className="truncate">Skill Registry</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {notificationsEnabled ? (
                <SidebarMenuItem>
                  <NotificationsWidget />
                </SidebarMenuItem>
              ) : null}
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Support"
                      className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                    >
                      <CircleHelp />
                      <span className="truncate">Support</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="right" className="min-w-52 rounded-lg p-1">
                    <DropdownMenuLabel className="max-w-64 whitespace-normal px-2 py-2 text-xs font-normal leading-5 text-muted-foreground">
                      Have an idea for a feature that could make your work easier or improve the Agent Hub for others?{' '}
                      <a
                        href={SUPPORT_SLACK_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-foreground underline underline-offset-4"
                      >
                        Share it in Slack
                      </a>.
                      {' '}
                      Your idea could shape the next capability.
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <a href={APP_HELP_DOC_URL} target="_blank" rel="noopener noreferrer">
                        <BookOpen />
                        Guide
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={APP_DEMO_URL} target="_blank" rel="noopener noreferrer">
                        <PlayCircle />
                        Introduction Video
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={SUPPORT_SLACK_URL} target="_blank" rel="noopener noreferrer">
                        <CircleHelp />
                        Slack Support
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="p-0">
          <SidebarGroupLabel className={cn('p-0', !showConversationSections && 'hidden')}>
            <div className="flex items-center gap-2 px-2.5 py-1">
              <History className="size-4" />
              <span>Conversations</span>
            </div>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className={cn(showConversationSections ? 'hidden group-data-[collapsible=icon]:block' : 'hidden')}>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Conversations"
                    className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                    onClick={() => setSidebarOpen(true)}
                  >
                    <History />
                    <span className="hidden">Conversations</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Shared Conversations"
                    className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                    onClick={() => setSidebarOpen(true)}
                  >
                    <Share2 />
                    <span className="hidden">Shared Conversations</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {schedulesEnabled ? (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip="Scheduled Workflows"
                      className={cn(FLAT_SIDEBAR_BUTTON_CLASS, COLLAPSED_ICON_BUTTON_CLASS)}
                      onClick={() => setSidebarOpen(true)}
                    >
                      <Clock />
                      <span className="hidden">Scheduled Workflows</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : null}
              </SidebarMenu>
            </div>
            <div className={cn('group-data-[collapsible=icon]:hidden', !showConversationSections && 'hidden')}>
              <div className="flex flex-col gap-3">
                <div className={SUBSECTION_CLASS}>
                  {isLoadingHistory ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="size-4 animate-spin" aria-label="Loading chat history" />
                    </div>
                  ) : filteredThreads.length === 0 ? (
                    <p className="px-2.5 text-xs text-muted-foreground">No conversations yet</p>
                  ) : (
                    <div className={SESSION_LIST_CLASS}>
                      {groupedThreads.map(({ label, threads: groupThreads }) => (
                        <div key={label}>
                          <div className={GROUP_LABEL_CLASS}>{label}</div>
                          {groupThreads.map((thread) => {
                            const isActive = thread.id === activeThreadId;
                            return (
                              <div
                                key={thread.id}
                                className={chatItemClass(isActive)}
                                title={thread.title ?? thread.id}
                                onClick={() => {
                                  const agentId = thread.agentId ?? selectedAgentId;
                                  if (!agentId) return;
                                  selectThread(agentId, thread.id);
                                }}
                              >
                                <div className={SESSION_ROW_INNER_CLASS}>
                                  <span
                                    className="inline-block size-2 shrink-0 rounded-full"
                                    // eslint-disable-next-line no-restricted-syntax -- devStatusColor() returns a dynamic hex string, not expressible as a static Tailwind class.
                                    style={{ backgroundColor: devStatusColor(statusMap.get(thread.agentId ?? '') ?? null) }}
                                  />
                                  <span className={sessionLabelClass(isActive)}>
                                    {thread.title ?? thread.id}
                                  </span>
                                  {thread.pinnedAt !== null && (
                                    <Pin className="mr-5 size-3 shrink-0 text-primary" aria-hidden="true" />
                                  )}
                                  <span className={SESSION_ACTION_CLASS} onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon-xs" aria-label="Chat actions">
                                          <MoreVertical />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          aria-label={thread.pinnedAt ? 'Unpin this chat' : 'Pin this chat'}
                                          onSelect={() => { void handlePinThread(thread); }}
                                        >
                                          {thread.pinnedAt ? <PinOff /> : <Pin />}
                                          {thread.pinnedAt ? 'Unpin' : 'Pin'}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          aria-label="Rename this chat"
                                          onSelect={() => openRenameDialog(thread)}
                                        >
                                          <Pencil />
                                          Rename
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          aria-label="Share this chat"
                                          onSelect={() => setShareThreadId(thread.id)}
                                        >
                                          <Share2 />
                                          Share
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          aria-label="Delete this chat"
                                          onSelect={() => setDeleteSingleConfirm(thread.id)}
                                        >
                                          <Trash2 />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                  {hasMoreThreads && (
                    <div className="px-2.5 pt-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start px-0 text-muted-foreground"
                        disabled={isLoadingMoreThreads}
                        onClick={() => { void fetchMoreThreads(); }}
                      >
                        {isLoadingMoreThreads && <Loader2 data-icon="inline-start" className="animate-spin" />}
                        {isLoadingMoreThreads ? 'Loading more conversations' : 'Load more conversations'}
                      </Button>
                    </div>
                  )}
                </div>
                <SidebarSeparator className="mx-2.5" />
                <div className={SUBSECTION_CLASS}>
                    <div className={SUBSECTION_HEADER_CLASS}>
                      <Share2 className="size-4" />
                      <span>Shared Conversations</span>
                    </div>
                  {isLoadingShared ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="size-4 animate-spin" aria-label="Loading shared chats" />
                    </div>
                  ) : filteredSharedThreads.length > 0 ? (
                    <>
                      <div className={SESSION_LIST_CLASS}>
                        {visibleSharedThreads.map((thread) => {
                          const isActive = thread.id === activeThreadId;
                          return (
                            <div
                              key={thread.id}
                              className={chatItemClass(isActive)}
                              title={thread.title ?? thread.id}
                              onClick={() => {
                                const agentId = thread.agentId ?? selectedAgentId;
                                if (!agentId) return;
                                selectThread(agentId, thread.id);
                              }}
                            >
                              <div className={SESSION_ROW_INNER_CLASS}>
                                <span
                                  className="inline-block size-2 shrink-0 rounded-full"
                                  // eslint-disable-next-line no-restricted-syntax -- devStatusColor() returns a dynamic hex string, not expressible as a static Tailwind class.
                                  style={{ backgroundColor: devStatusColor(statusMap.get(thread.agentId ?? '') ?? null) }}
                                />
                                <span className={sessionLabelClass(isActive)}>
                                  {thread.title ?? thread.id}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {hasMoreSharedThreads && (
                        <div className="px-2.5 pt-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start px-0 text-muted-foreground"
                            onClick={() => setSharedLimit((n) => n + PAGE_SIZE)}
                          >
                            Load more shared conversations
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="px-2.5 text-xs text-muted-foreground">Nothing shared with you yet</p>
                  )}
                </div>
                {schedulesEnabled ? <SidebarSeparator className="mx-2.5" /> : null}
                {schedulesEnabled ? <div className={SUBSECTION_CLASS}>
                  <div className={SUBSECTION_HEADER_CLASS}>
                    <History className="size-4" />
                    <span>Scheduled Workflows</span>
                  </div>
                  {schedulesQuery.isLoading ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="size-4 animate-spin" aria-label="Loading workflows" />
                    </div>
                  ) : schedulesQuery.isError ? (
                    <div
                      className="mx-2.5 rounded-lg border border-status-warning/30 bg-status-warning-subtle px-3 py-2"
                      role="alert"
                    >
                      <p className="text-xs text-muted-foreground">Unable to load workflows.</p>
                      <div className="mt-2 flex gap-1.5">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            void schedulesQuery.refetch();
                          }}
                        >
                          Retry
                        </Button>
                      </div>
                    </div>
                  ) : jobs.length > 0 ? (
                    <>
                      <div className={SESSION_LIST_CLASS} aria-label="Scheduled workflows">
                        {visibleJobs.map((job) => (
                          <div key={job.id} className={cn(chatItemClass(false), 'min-w-0')} title={job.name}>
                            <div className={SESSION_ROW_INNER_CLASS}>
                              <span className={sessionLabelClass(false)}>{job.name}</span>
                              <span onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  className={DELETE_ACTION_CLASS}
                                  onClick={() => setDeleteJobConfirm(job.id)}
                                  aria-label={`Delete workflow ${job.name}`}
                                >
                                  <Trash2 />
                                </Button>
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{formatScheduledTime(job)}</p>
                          </div>
                        ))}
                      </div>
                      {hasMoreJobs && (
                        <div className="px-2.5 pt-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start px-0 text-muted-foreground"
                            onClick={() => setJobsLimit((n) => n + PAGE_SIZE)}
                          >
                            Load more workflows
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="px-2.5 text-xs text-muted-foreground">No scheduled workflows</p>
                  )}
                </div> : null}
              </div>
            </div>
            <ConfirmDialog
              isOpen={deleteSingleConfirm !== null}
              title="Delete this conversation?"
              confirmLabel="Delete"
              onCancel={() => setDeleteSingleConfirm(null)}
              onConfirm={() => {
                const thread = filteredThreads.find((t) => t.id === deleteSingleConfirm);
                setDeleteSingleConfirm(null);
                if (thread) void handleDeleteThread(thread);
              }}
            >
              This action cannot be undone.
            </ConfirmDialog>
            <ConfirmDialog
              isOpen={deleteJobConfirm !== null}
              title="Delete and unschedule this workflow?"
              confirmLabel="Delete workflow"
              onCancel={() => setDeleteJobConfirm(null)}
              onConfirm={() => {
                const jobId = deleteJobConfirm;
                setDeleteJobConfirm(null);
                if (jobId) {
                  void cancelSchedule(jobId).catch((err) => {
                    log.warn('schedule_cancel_failed', { error: err });
                  });
                }
              }}
            >
              This action cannot be undone.
            </ConfirmDialog>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border pl-2.5 pr-2 py-3 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        <FooterControls />
      </SidebarFooter>
      <ShareThreadDialog
        threadId={shareThreadId ?? ''}
        isOpen={shareThreadId !== null}
        onClose={() => setShareThreadId(null)}
      />
      <Dialog
        open={renameThreadId !== null}
        onOpenChange={(open) => {
          if (!open) setRenameThreadId(null);
        }}
      >
        <DialogContent aria-label="Rename chat">
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleRenameThread();
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename chat</DialogTitle>
              <DialogDescription>Change the conversation name shown in history.</DialogDescription>
            </DialogHeader>
            <Input
              autoFocus
              aria-label="Conversation name"
              value={renameDraft}
              onChange={(event) => setRenameDraft(event.target.value)}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameThreadId(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renameDraft.trim().length === 0}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
